import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import './ContactModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: Props): React.JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSubmitted(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) firstInputRef.current?.focus();
  }, [isOpen]);

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="contact-modal__backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            className="contact-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-modal-title"
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 16 }}
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <button className="contact-modal__close" onClick={onClose} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>

            {submitted ? (
              <motion.div
                className="contact-modal__success"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <p className="contact-modal__success-label">Message sent</p>
                <h2 className="contact-modal__success-heading">We'll be in touch shortly.</h2>
                <p className="contact-modal__success-body">Thank you for reaching out to Everfield. Our team will review your enquiry and respond within 1–2 business days.</p>
              </motion.div>
            ) : (
              <>
                <p className="contact-modal__label">Contact</p>
                <h2 id="contact-modal-title" className="contact-modal__heading">Get in touch</h2>
                <p className="contact-modal__sub">Interested in partnering with Everfield? Leave your details and we'll reach out.</p>
                <form className="contact-modal__form" onSubmit={handleSubmit} noValidate>
                  <div className="contact-modal__row">
                    <div className="contact-modal__field">
                      <label htmlFor="cm-name">Full name</label>
                      <input id="cm-name" type="text" placeholder="Jane Smith" required ref={firstInputRef} />
                    </div>
                    <div className="contact-modal__field">
                      <label htmlFor="cm-email">Email address</label>
                      <input id="cm-email" type="email" placeholder="jane@example.com" required />
                    </div>
                  </div>
                  <div className="contact-modal__field">
                    <label htmlFor="cm-org">Organisation</label>
                    <input id="cm-org" type="text" placeholder="Company or land trust name" />
                  </div>
                  <div className="contact-modal__field">
                    <label htmlFor="cm-message">Message</label>
                    <textarea id="cm-message" rows={4} placeholder="Tell us about your project or enquiry…" required />
                  </div>
                  <button className="contact-modal__submit" type="submit">Send message</button>
                </form>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
